<template>
  <!--
    表格组件模板结构：
    1. 表格容器 - 支持焦点与键盘事件
    2. 表格包装器 - 承载数据表格
    3. 数据表格 - 渲染行列、合并单元格、背景色与标题行列样式
  -->
  <div class="id_12">
    <!-- 表格容器 -->
    <div
      class="table-container"
      :class="{ 'full-width': isFullWidth }"
      style="max-height: max-content !important;"
      ref="tableContainer"
      @keydown.stop="handleKeyDown"
      @focus.stop="onTableFocus"
      @blur.stop="onTableBlur"
      @click.stop="onTableContainerClick"
      tabindex="0"
    >
      <!-- 表格包装器 -->
      <div v-if="tableData.length > 0" class="table-wrapper" ref="tableWrapper">
        <!-- 数据表格 -->
        <table
          class="data-table"
          ref="dataTable"
          role="grid"
          aria-label="数据表格"
        >
          <colgroup>
            <col
              v-for="(width, colIndex) in colWidths"
              :key="'col-' + colIndex"
              :style="{ width: isFullWidth ? 'auto' : width + 'px' }"
            >
          </colgroup>
          <tbody>
            <tr
              v-for="(row, rowIndex) in tableData"
              :key="'row-' + rowIndex"
              role="row"
              :class="{ 'header-row': rowIndex === 0 && hasHeaderRow }"
              :style="{ height: isFullWidth ? 'auto' : rowHeights[rowIndex] + 'px' }"
            >
              <td
                v-for="(cell, colIndex) in row"
                v-show="shouldShowCell(rowIndex, colIndex)"
                :key="'cell-' + rowIndex + '-' + colIndex"
                :class="{
                  selected: isCellSelected(rowIndex, colIndex),
                  'drag-selected': isCellInDragSelection(rowIndex, colIndex),
                  'header-cell': rowIndex === 0 && hasHeaderRow,
                  'header-col-cell': colIndex === 0 && hasHeaderCol,
                  'corner-cell': rowIndex === 0 && colIndex === 0 && hasHeaderRow && hasHeaderCol,
                  'top-left-corner': rowIndex === 0 && colIndex === 0,
                  'top-right-corner': rowIndex === 0 && colIndex === colCount - 1,
                  'bottom-left-corner': rowIndex === rowCount - 1 && colIndex === 0,
                  'bottom-right-corner': rowIndex === rowCount - 1 && colIndex === colCount - 1,
                  'merged-cell': getMergeSpan(rowIndex, colIndex)
                }"
                :style="getCellStyle(rowIndex, colIndex)"
                :rowspan="getMergeRowSpan(rowIndex, colIndex)"
                :colspan="getMergeColSpan(rowIndex, colIndex)"
                :data-row-index="rowIndex"
                :data-col-index="colIndex"
                :tabindex="0"
                :aria-selected="isCellSelected(rowIndex, colIndex)"
                :aria-label="`单元格 ${rowIndex + 1}-${colIndex + 1}`"
                :role="rowIndex === 0 && hasHeaderRow ? 'columnheader' : 'gridcell'"
              >
                <!-- 单元格内容 -->
                <div
                  class="cell-content"
                  v-html="cell.content"
                ></div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * @file id_12.vue
 * @description 表格展示组件 - 根据 props.data 渲染表格，并对外提供 saveState 同步状态
 *
 * 【主要功能】
 * 1. 表格渲染 - 展示单元格内容、标题行/列、圆角四角样式
 * 2. 合并单元格 - 通过 mergedCells 控制 rowspan/colspan 与覆盖格隐藏
 * 3. 背景色 - 支持单元格 / 列 / 行三级背景，优先级见 getCellStyle
 * 4. 布局 - 支持列宽、行高、自适应页宽（isFullWidth）
 * 5. 状态同步 - saveState 将内部状态写回 props.data，供父组件序列化
 *
 * 【数据存储结构】
 * - tableData: 二维数组，每个单元格为 { content: String, bgColor: String }
 * - rowBgColors: 行背景色映射（key 为行索引字符串）
 * - colBgColors: 列背景色映射（key 为列索引字符串）
 * - cellBgColors: 单元格背景色映射（key 为 "row-col"）
 * - mergedCells: 合并区域数组 { row, col, rowSpan, colSpan }
 *
 * 【注意事项】
 * 1. 背景色优先级：单元格 bgColor / cellBgColors > 列背景 > 行背景
 * 2. 写入 props.data 时使用 JSON 深拷贝，避免引用污染
 * 3. 圆角依赖 border-collapse: separate + 四角 class
 */
export default {
  name: 'id_12',
  props: {
    /**
     * @prop {Object} data - 表格数据对象，用于保存和恢复表格状态
     * @property {Array} data.table - 表格数据（二维数组）
     * @property {Object} data.rowBgColors - 行背景颜色映射
     * @property {Object} data.colBgColors - 列背景颜色映射
     * @property {Object} data.cellBgColors - 单元格背景颜色映射
     * @property {Boolean} data.isFullWidth - 是否自适应页宽
     * @property {Boolean} data.hasHeaderRow - 是否有标题行
     * @property {Boolean} data.hasHeaderCol - 是否有标题列
     * @property {Array} data.colWidths - 列宽数组（像素）
     * @property {Array} data.rowHeights - 行高数组（像素）
     * @property {Array} data.mergedCells - 合并单元格数组
     */
    data: Object
  },
  data () {
    return {
      /**
       * @property {Array} tableData - 表格数据（二维数组）
       * 每个单元格对象：{ content: String, bgColor: String }
       */
      tableData: [],
      /**
       * @property {Array} selectedRows - 选中的行索引数组
       */
      selectedRows: [],
      /**
       * @property {Array} selectedCols - 选中的列索引数组
       */
      selectedCols: [],
      /**
       * @property {Array} selectedCells - 选中的单元格键数组（格式："row-col"）
       */
      selectedCells: [],
      /**
       * @property {Boolean} isDragging - 是否正在拖动选择
       */
      isDragging: false,
      /**
       * @property {Boolean} isFullWidth - 是否自适应页宽
       */
      isFullWidth: false,
      /**
       * @property {Boolean} hasHeaderRow - 是否有标题行
       */
      hasHeaderRow: true,
      /**
       * @property {Boolean} hasHeaderCol - 是否有标题列
       */
      hasHeaderCol: false,
      /**
       * @property {Object} rowBgColors - 行背景颜色映射（key: 行索引字符串）
       */
      rowBgColors: {},
      /**
       * @property {Object} colBgColors - 列背景颜色映射（key: 列索引字符串）
       */
      colBgColors: {},
      /**
       * @property {Object} cellBgColors - 单元格背景颜色映射（key: "row-col"）
       */
      cellBgColors: {},
      /**
       * @property {Array} colWidths - 列宽数组（像素值），默认每列 80px
       */
      colWidths: [],
      /**
       * @property {Array} rowHeights - 行高数组（像素值），默认每行 40px
       */
      rowHeights: [],
      /**
       * @property {Array} mergedCells - 合并单元格数组
       * 每个合并对象：{ row: 起始行, col: 起始列, rowSpan: 跨行数, colSpan: 跨列数 }
       */
      mergedCells: []
    }
  },
  computed: {
    /**
     * @computed {Number} rowCount - 表格行数
     * @returns {Number}
     */
    rowCount () {
      return this.tableData.length
    },
    /**
     * @computed {Number} colCount - 表格列数
     * @returns {Number}
     */
    colCount () {
      return this.tableData.length > 0 ? this.tableData[0].length : 0
    }
  },
  watch: {
    /**
     * @watch {'data.table'} - 监听外部传入的表格数据变化
     * @param {Array} val - 新的表格数据
     */
    'data.table' (val) {
      if (val) {
        this.tableData = JSON.parse(JSON.stringify(val))
        this.ensureLayoutArrays()
      }
    }
  },
  mounted () {
    // 从 props.data 初始化内部状态
    this.initFromData()
  },
  methods: {
    /**
     * @method initFromData - 从 props.data 初始化内部状态
     * @description 深拷贝外部数据到组件内部，并补齐列宽/行高默认值
     */
    initFromData () {
      if (!this.data) return

      if (this.data.table) {
        this.tableData = JSON.parse(JSON.stringify(this.data.table))
      }
      if (this.data.rowBgColors) {
        this.rowBgColors = JSON.parse(JSON.stringify(this.data.rowBgColors))
      }
      if (this.data.colBgColors) {
        this.colBgColors = JSON.parse(JSON.stringify(this.data.colBgColors))
      }
      if (this.data.cellBgColors) {
        this.cellBgColors = JSON.parse(JSON.stringify(this.data.cellBgColors))
      }
      if (this.data.isFullWidth !== undefined) {
        this.isFullWidth = this.data.isFullWidth
      }
      if (this.data.hasHeaderRow !== undefined) {
        this.hasHeaderRow = this.data.hasHeaderRow
      }
      if (this.data.hasHeaderCol !== undefined) {
        this.hasHeaderCol = this.data.hasHeaderCol
      }
      // 初始化列宽数据
      if (this.data.colWidths && Array.isArray(this.data.colWidths)) {
        this.colWidths = JSON.parse(JSON.stringify(this.data.colWidths))
      }
      // 初始化行高数据
      if (this.data.rowHeights && Array.isArray(this.data.rowHeights)) {
        this.rowHeights = JSON.parse(JSON.stringify(this.data.rowHeights))
      }
      // 初始化合并单元格数据
      if (this.data.mergedCells && Array.isArray(this.data.mergedCells)) {
        this.mergedCells = JSON.parse(JSON.stringify(this.data.mergedCells))
      }
      this.ensureLayoutArrays()
    },

    /**
     * @method ensureLayoutArrays - 确保列宽/行高数组与当前表格尺寸一致
     * @description 缺失或长度不匹配时，列宽默认 80px，行高默认 40px
     */
    ensureLayoutArrays () {
      if (this.tableData.length > 0) {
        const cols = this.tableData[0].length
        if (!this.colWidths.length || this.colWidths.length !== cols) {
          this.colWidths = Array(cols).fill(80)
        }
        if (!this.rowHeights.length || this.rowHeights.length !== this.tableData.length) {
          this.rowHeights = this.tableData.map(() => 40)
        }
      }
    },

    /**
     * @method isCellSelected - 判断单元格是否被选中
     * @param {Number} rowIndex - 行索引
     * @param {Number} colIndex - 列索引
     * @returns {Boolean} 是否被选中（行选 / 列选 / 单元格选任一命中）
     */
    isCellSelected (rowIndex, colIndex) {
      const cellKey = `${rowIndex}-${colIndex}`
      return this.selectedCells.indexOf(cellKey) > -1 ||
             this.selectedRows.indexOf(rowIndex) > -1 ||
             this.selectedCols.indexOf(colIndex) > -1
    },

    /**
     * @method isCellInDragSelection - 判断单元格是否在拖动选择区域内
     * @param {Number} rowIndex - 行索引
     * @param {Number} colIndex - 列索引
     * @returns {Boolean} 是否在拖动选择矩形范围内
     */
    isCellInDragSelection (rowIndex, colIndex) {
      if (!this.isDragging || !this._dragStart || !this._dragEnd) return false
      const startRow = Math.min(this._dragStart.row, this._dragEnd.row)
      const endRow = Math.max(this._dragStart.row, this._dragEnd.row)
      const startCol = Math.min(this._dragStart.col, this._dragEnd.col)
      const endCol = Math.max(this._dragStart.col, this._dragEnd.col)
      return rowIndex >= startRow && rowIndex <= endRow &&
             colIndex >= startCol && colIndex <= endCol
    },

    /**
     * @method clearSelection - 清除所有选择
     */
    clearSelection () {
      this.selectedRows = []
      this.selectedCols = []
      this.selectedCells = []
    },

    /**
     * @method getMergeInfo - 获取单元格的合并信息
     * @param {Number} rowIndex - 行索引
     * @param {Number} colIndex - 列索引
     * @returns {Object|null} 合并信息对象，或非起始格时返回 null
     */
    getMergeInfo (rowIndex, colIndex) {
      return this.mergedCells.find(m => m.row === rowIndex && m.col === colIndex) || null
    },

    /**
     * @method shouldShowCell - 单元格是否应该显示
     * @description 合并区域内被覆盖的非起始单元格不显示（v-show=false）
     * @param {Number} rowIndex - 行索引
     * @param {Number} colIndex - 列索引
     * @returns {Boolean} 是否显示
     */
    shouldShowCell (rowIndex, colIndex) {
      for (let i = 0; i < this.mergedCells.length; i++) {
        const merge = this.mergedCells[i]
        if (merge.rowSpan > 1 || merge.colSpan > 1) {
          const endRow = merge.row + merge.rowSpan - 1
          const endCol = merge.col + merge.colSpan - 1
          // 若在合并区域内但不是起始点，则隐藏
          if (rowIndex >= merge.row && rowIndex <= endRow &&
              colIndex >= merge.col && colIndex <= endCol) {
            return rowIndex === merge.row && colIndex === merge.col
          }
        }
      }
      return true
    },

    /**
     * @method getMergeSpan - 当前单元格是否为合并起始格
     * @param {Number} rowIndex - 行索引
     * @param {Number} colIndex - 列索引
     * @returns {Boolean}
     */
    getMergeSpan (rowIndex, colIndex) {
      const merge = this.getMergeInfo(rowIndex, colIndex)
      return !!merge && (merge.rowSpan > 1 || merge.colSpan > 1)
    },

    /**
     * @method getMergeRowSpan - 获取单元格的 rowspan
     * @param {Number} rowIndex - 行索引
     * @param {Number} colIndex - 列索引
     * @returns {Number} rowspan，无合并时为 1
     */
    getMergeRowSpan (rowIndex, colIndex) {
      const merge = this.getMergeInfo(rowIndex, colIndex)
      return merge ? merge.rowSpan : 1
    },

    /**
     * @method getMergeColSpan - 获取单元格的 colspan
     * @param {Number} rowIndex - 行索引
     * @param {Number} colIndex - 列索引
     * @returns {Number} colspan，无合并时为 1
     */
    getMergeColSpan (rowIndex, colIndex) {
      const merge = this.getMergeInfo(rowIndex, colIndex)
      return merge ? merge.colSpan : 1
    },

    /**
     * @method getCellStyle - 获取单元格样式
     * @param {Number} rowIndex - 行索引
     * @param {Number} colIndex - 列索引
     * @returns {Object} 单元格样式对象
     * @description 背景色优先级：单元格数据 bgColor > cellBgColors > colBgColors > rowBgColors
     */
    getCellStyle (rowIndex, colIndex) {
      const cellKey = `${rowIndex}-${colIndex}`
      const cellData = this.tableData[rowIndex] && this.tableData[rowIndex][colIndex]
      const dataColor = cellData && cellData.bgColor
      const cellColor = this.cellBgColors[cellKey]
      const rowColor = this.rowBgColors[String(rowIndex)]
      const colColor = this.colBgColors[String(colIndex)]
      const bgColor = dataColor || cellColor || colColor || rowColor || ''
      return bgColor ? { background: bgColor + ' !important' } : {}
    },

    /**
     * @method handleKeyDown - 表格容器键盘按下处理
     * @param {Event} event - 事件对象
     * @description Escape 清除当前选择
     */
    handleKeyDown (event) {
      if (event.key === 'Escape') {
        this.clearSelection()
      }
    },

    /**
     * @method onTableFocus - 表格获得焦点处理
     * @description 保留事件绑定入口，便于后续扩展焦点态逻辑
     */
    onTableFocus () {},

    /**
     * @method onTableBlur - 表格失去焦点处理
     * @description 保留事件绑定入口，便于后续扩展失焦逻辑
     */
    onTableBlur () {},

    /**
     * @method onTableContainerClick - 表格容器点击处理
     * @param {Event} event - 事件对象
     * @description 点击非单元格区域时聚焦容器；点击单元格本身时不抢焦点
     */
    onTableContainerClick (event) {
      if (event.target.closest('td, th')) {
        return
      }
      if (this.$refs.tableContainer) {
        this.$refs.tableContainer.focus()
      }
    },

    /**
     * @method saveState - 保存表格状态
     * @description 将表格数据和样式深拷贝写回 props.data，供父组件序列化调用
     * @note 使用 JSON.parse(JSON.stringify()) 深拷贝，避免引用传递导致的数据污染
     */
    saveState () {
      if (this.data) {
        this.data.table = JSON.parse(JSON.stringify(this.tableData))
        this.data.rowBgColors = JSON.parse(JSON.stringify(this.rowBgColors))
        this.data.colBgColors = JSON.parse(JSON.stringify(this.colBgColors))
        this.data.cellBgColors = JSON.parse(JSON.stringify(this.cellBgColors))
        this.data.isFullWidth = this.isFullWidth
        this.data.hasHeaderRow = this.hasHeaderRow
        this.data.hasHeaderCol = this.hasHeaderCol
        this.data.colWidths = JSON.parse(JSON.stringify(this.colWidths))
        this.data.rowHeights = JSON.parse(JSON.stringify(this.rowHeights))
        this.data.mergedCells = JSON.parse(JSON.stringify(this.mergedCells))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/**
 * 表格组件样式
 * - border-collapse: separate 以支持圆角
 * - td::before 用于选中态悬浮层（不影响单元格背景色）
 */
/* 滚动条样式 */
::-webkit-scrollbar {
  height: 6px;
  display: inline-block !important;
}
::-webkit-scrollbar-thumb {
  height: 6px;
  border-radius: 10px;
  background: #D4D4D4;
  -webkit-box-shadow: inset006pxrgba(0,0,0,0.5);
}
.id_12 {
  padding: 12px 0;
  padding-top: 32px;
  background-color: transparent;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  overflow-x: auto;

  /** 表格容器；full-width 时表格撑满父级宽度 */
  .table-container {
    position: relative;
    outline: none;
    overflow: visible;
    &.full-width {
      .table-wrapper {
        width: 100%;
        .data-table { width: 100%; }
      }
    }
  }

  /** 表格包装与数据表 */
  .table-wrapper {
    position: relative;
    overflow: auto;
    .data-table {
      border-collapse: separate;
      border-spacing: 0;
      min-width: 300px;
      background: #fff;
      border-radius: 8px;
      box-sizing: border-box;
      .header-row { background-color: #f5f6f7; }
      td {
        border: 1px solid #DEDEDF;
        min-width: 80px;
        height: 44px;
        box-sizing: border-box;
        cursor: text;
        outline: none;
        vertical-align: top;
        position: relative;
        overflow: visible;
        /** 选中态悬浮层：不影响单元格原有背景色 */
        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(63, 133, 255, 0.15);
          border: 2px solid rgba(63, 133, 255, 0.6);
          opacity: 0;
          transition: opacity 0.15s ease, border-color 0.15s ease;
          pointer-events: none;
          z-index: 1;
        }
        &.header-cell,
        &.header-col-cell {
          background-color: #f5f6f7;
          font-weight: 600;
          text-align: center;
          min-width: 60px;
          height: 36px;
        }
        &.corner-cell {
          background-color: #f5f6f7;
          font-weight: 600;
          text-align: center;
          width: 60px;
          height: 36px;
        }
        &.selected, &.drag-selected {
          &::before { opacity: 1; }
        }
        &.drag-selected {
          &::before {
            background: rgba(63, 133, 255, 0.1);
            border-color: rgba(63, 133, 255, 0.4);
          }
        }
        /** 四角圆角 */
        &.top-left-corner {
          border-top-left-radius: 6px;
          border-top: 2px solid #DEDEDF;
          border-left: 2px solid #DEDEDF;
        }
        &.top-right-corner {
          border-top-right-radius: 6px;
          border-top: 2px solid #DEDEDF;
          border-right: 2px solid #DEDEDF;
        }
        &.bottom-left-corner {
          border-bottom-left-radius: 6px;
          border-bottom: 2px solid #DEDEDF;
          border-left: 2px solid #DEDEDF;
        }
        &.bottom-right-corner {
          border-bottom-right-radius: 6px;
          border-bottom: 2px solid #DEDEDF;
          border-right: 2px solid #DEDEDF;
        }
        /** 单元格内容容器 */
        .cell-content {
          padding: 12px 16px;
          border: none;
          outline: none;
          position: relative;
          z-index: 1;
          min-height: 20px;
          word-break: break-word;
          white-space: pre-wrap;
        }
      }
      tr:first-child td { border-top: 2px solid #DEDEDF; }
      tr:last-child td { border-bottom: 2px solid #DEDEDF; }
      tr td:first-child { border-left: 2px solid #DEDEDF; }
      tr td:last-child { border-right: 2px solid #DEDEDF; }
      tr.selected td::before { opacity: 1; }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/module/course/public/id_12.vue